/**
 * Outbound calls — the shared vocabulary (schema `outbound`). Pure: the routes,
 * the components and the service all read it.
 *
 * THE OWNER (2026-10-07): *"I need the customer service reps to be able to do
 * outbound calls based off of an excel spreadsheet… every single CSR that is
 * logged in to have a task that says Claim Outbound Call where if they click
 * it then it will assign them the customer from this list and it will let them
 * classify the call. It should also have a type this way in the future we can
 * automate other outbound lists… Once a csr claims a call it should get
 * removed from the list and it should generate a task that will close
 * whenever the csr classifies the call."*
 *
 *   lists    one call list: a name, a TYPE (LIST_TYPES below), the manager's
 *            prompt (what the CSRs should do on these calls), a status
 *   entries  one customer row of a list: queued → claimed (a CSR holds it, a
 *            task is open for it) → completed (classified); `skipped` rows
 *            never entered the queue (no usable phone, Do Not Service, a
 *            duplicate) and say why
 *   events   the journal: who did what to which list or entry, and when
 */

/** The task-pool department whose members see "Claim Outbound Call". */
export const OUTBOUND_CALLS_DEPARTMENT = 'outbound_calls'

export const LIST_STATUSES = ['draft', 'active', 'paused', 'archived'] as const
export type ListStatus = (typeof LIST_STATUSES)[number]

export const ENTRY_STATUSES = [
	'queued',
	'claimed',
	'completed',
	'skipped',
] as const
export type EntryStatus = (typeof ENTRY_STATUSES)[number]

export type Outcome = {
	value: string
	label: string
	hint: string
	/** "Call back later": the CSR must say when */
	needsCallbackAt?: boolean
	/** counts toward a list's "jobs booked" quota (pacing.ts) */
	countsAsBooked?: boolean
}

/**
 * The outcomes a CSR picks from when they classify an outbound call. One
 * shared set today; a future list type can bring its own.
 *
 * "Job booked" and "Interested – follow up" were one outcome until the owner
 * asked for a jobs-booked quota (2026-10-07): only "Job booked" counts toward
 * it.
 */
export const STANDARD_OUTCOMES: ReadonlyArray<Outcome> = [
	{
		value: 'booked',
		label: 'Job booked',
		hint: 'A job was booked on this call',
		countsAsBooked: true,
	},
	{
		value: 'interested',
		label: 'Interested – follow up',
		hint: 'They want the work but nothing is booked yet',
	},
	{
		value: 'not_interested',
		label: 'Reached – not interested',
		hint: 'Talked to them; they said no',
	},
	{
		value: 'voicemail',
		label: 'Left voicemail',
		hint: 'No answer — you left a message',
	},
	{
		value: 'no_answer',
		label: 'No answer',
		hint: 'Nobody picked up and no message was left',
	},
	{
		value: 'wrong_number',
		label: 'Wrong number',
		hint: 'The number does not reach this customer',
	},
	{
		value: 'do_not_call',
		label: 'Do not call',
		hint: 'They asked us not to call them again',
	},
	{
		value: 'call_back',
		label: 'Call back later',
		hint: 'They asked for a call at another time — pick when',
		needsCallbackAt: true,
	},
]

export type ListType = {
	value: string
	label: string
	/** what a manager reads when picking it */
	description: string
	outcomes: ReadonlyArray<Outcome>
	/** a manager can create one by uploading a spreadsheet */
	uploadable: boolean
}

/**
 * THE TYPE REGISTRY. A list's `type` names one of these. Today there is one —
 * a manager uploads a spreadsheet — and a future automated list (built from
 * the lake, say) is a new entry here with its own outcomes; nothing in the
 * schema has to change.
 */
export const LIST_TYPES: ReadonlyArray<ListType> = [
	{
		value: 'manual_upload',
		label: 'Uploaded spreadsheet',
		description: 'A manager uploaded an Excel or CSV list of customers.',
		outcomes: STANDARD_OUTCOMES,
		uploadable: true,
	},
]

export function listType(value: string): ListType | null {
	return LIST_TYPES.find((t) => t.value === value) ?? null
}

/** The outcomes a list of this type offers (the standard set for an unknown type). */
export function outcomesFor(type: string): ReadonlyArray<Outcome> {
	return listType(type)?.outcomes ?? STANDARD_OUTCOMES
}

export function outcomeLabel(type: string, value: string | null): string {
	if (!value) return ''
	return (
		outcomesFor(type).find((o) => o.value === value)?.label ??
		value.replace(/_/g, ' ')
	)
}

/** One extra spreadsheet column on an entry, in the sheet's own column order. */
export type DetailValue = string | number | boolean
export type Detail = { label: string; value: DetailValue }

/** Flags shown on an entry without keeping it out of the queue. */
export const ENTRY_FLAGS = {
	bought_elsewhere: 'Bought a system at another location',
} as const
export type EntryFlag = keyof typeof ENTRY_FLAGS

/** What the mapper reads each recognised column as (mapping.ts). */
export type FieldKey =
	| 'name'
	| 'first_name'
	| 'last_name'
	| 'phone'
	| 'email'
	| 'street'
	| 'city'
	| 'state'
	| 'zip'
	| 'st_customer_id'
	| 'st_location_id'
	| 'st_job_number'
	| 'do_not_service'
	| 'bought_elsewhere'

/** What the preview calls each recognised column. */
export const FIELD_LABELS: Record<FieldKey, string> = {
	name: 'Customer name',
	first_name: 'First name',
	last_name: 'Last name',
	phone: 'Phone',
	email: 'Email',
	street: 'Street',
	city: 'City',
	state: 'State',
	zip: 'Zip',
	st_customer_id: 'ServiceTitan customer ID',
	st_location_id: 'ServiceTitan location ID',
	st_job_number: 'Job number',
	do_not_service: 'Do Not Service (skip)',
	bought_elsewhere: 'Bought elsewhere (flag)',
}
